import React, { useState, useEffect, useCallback } from 'react';
import { colabApi } from '../lib/colabApi';
import { formatDate } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Star, Loader2, LogOut, Wrench, User, ClipboardList, KeyRound } from 'lucide-react';

const Login = ({ onLogged }) => {
  const [telefone, setTelefone] = useState('');
  const [senha, setSenha] = useState('');
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const r = await colabApi.login(telefone, senha);
      localStorage.setItem('colab_token', r.data.access_token);
      onLogged();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Erro ao entrar');
    } finally {
      setLoading(false);
    }
  };
  return (
    <div className="min-h-screen flex items-center justify-center bg-emerald-50 px-4">
      <Card className="w-full max-w-sm">
        <CardHeader>
          <CardTitle className="text-center text-emerald-800">Área do Colaborador</CardTitle>
          <p className="text-center text-sm text-gray-500">Tenance Assistência Residencial</p>
        </CardHeader>
        <CardContent>
          <form onSubmit={submit} className="space-y-3">
            <div className="space-y-1">
              <Label>Telefone (com DDD)</Label>
              <Input value={telefone} onChange={(e) => setTelefone(e.target.value)} placeholder="31 99999-9999" data-testid="colab-login-telefone" />
            </div>
            <div className="space-y-1">
              <Label>Senha</Label>
              <Input type="password" value={senha} onChange={(e) => setSenha(e.target.value)} data-testid="colab-login-senha" />
            </div>
            <Button type="submit" disabled={loading} className="w-full text-white" style={{ backgroundColor: '#2B7A3D' }} data-testid="colab-login-btn">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Entrar'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

const STATUS_COLORS = { 'Concluído': 'bg-emerald-100 text-emerald-700', 'Agendado': 'bg-blue-100 text-blue-700', 'Pendente pagamento': 'bg-amber-100 text-amber-700', 'Cancelado': 'bg-red-100 text-red-600' };

export default function ColabPortal() {
  const [logged, setLogged] = useState(!!localStorage.getItem('colab_token'));
  const [me, setMe] = useState(null);
  const [servicos, setServicos] = useState([]);
  const [ordens, setOrdens] = useState([]);
  const [salvando, setSalvando] = useState(false);
  const [perfil, setPerfil] = useState({ telefone: '', pix_chave: '', pix_tipo: '' });
  const [senhas, setSenhas] = useState({ atual: '', nova: '' });

  const load = useCallback(async () => {
    try {
      const [meR, srvR, osR] = await Promise.all([colabApi.me(), colabApi.servicos(), colabApi.ordens()]);
      setMe(meR.data);
      setServicos(srvR.data);
      setOrdens(osR.data);
      setPerfil({ telefone: meR.data.telefone || '', pix_chave: meR.data.pix_chave || '', pix_tipo: meR.data.pix_tipo || '' });
    } catch (err) { /* 401 tratado no interceptor */ }
  }, []);

  useEffect(() => { if (logged) load(); }, [logged, load]);

  if (!logged) return <Login onLogged={() => setLogged(true)} />;
  if (!me) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-emerald-600" /></div>;

  const toggleServico = (id) => setServicos((prev) => prev.map((s) => (s.id === id ? { ...s, marcado: !s.marcado } : s)));
  const grupos = servicos.reduce((acc, s) => { (acc[s.categoria] = acc[s.categoria] || []).push(s); return acc; }, {});
  const totalMarcados = servicos.filter((s) => s.marcado).length;

  const salvarServicos = async () => {
    setSalvando(true);
    try {
      await colabApi.updateServicos(servicos.filter((s) => s.marcado).map((s) => s.id));
      toast.success('Serviços atualizados!');
    } catch { toast.error('Erro ao salvar'); } finally { setSalvando(false); }
  };

  const salvarPerfil = async () => {
    try {
      await colabApi.updatePerfil(perfil);
      toast.success('Dados atualizados!');
    } catch { toast.error('Erro ao salvar'); }
  };

  const alterarSenha = async () => {
    if (senhas.nova.length < 6) { toast.error('Nova senha: mínimo 6 caracteres'); return; }
    try {
      await colabApi.alterarSenha(senhas.atual, senhas.nova);
      toast.success('Senha alterada!');
      setSenhas({ atual: '', nova: '' });
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Erro ao alterar senha');
    }
  };

  const sair = () => { localStorage.removeItem('colab_token'); setLogged(false); setMe(null); };

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-emerald-800 text-white px-4 py-3 flex items-center justify-between">
        <div>
          <p className="font-semibold" data-testid="colab-nome">{me.nome}</p>
          <p className="text-xs text-emerald-200 flex items-center gap-1">
            <Star size={12} className="fill-amber-300 text-amber-300" /> Nota {me.nota?.toFixed(1)} · {me.os_concluidas} OS concluídas
          </p>
        </div>
        <Button variant="ghost" size="sm" onClick={sair} className="text-white hover:bg-emerald-700" data-testid="colab-sair-btn"><LogOut size={16} /></Button>
      </header>
      {me.senha_temporaria && (
        <div className="bg-amber-100 text-amber-800 text-sm px-4 py-2 text-center">Você está com senha temporária — troque na aba "Meus Dados".</div>
      )}
      <main className="max-w-2xl mx-auto p-4">
        <Tabs defaultValue="servicos">
          <TabsList className="w-full grid grid-cols-3">
            <TabsTrigger value="servicos" data-testid="tab-servicos"><Wrench size={14} className="mr-1" /> Serviços</TabsTrigger>
            <TabsTrigger value="dados" data-testid="tab-dados"><User size={14} className="mr-1" /> Meus Dados</TabsTrigger>
            <TabsTrigger value="os" data-testid="tab-os"><ClipboardList size={14} className="mr-1" /> Minhas OS</TabsTrigger>
          </TabsList>

          <TabsContent value="servicos" className="space-y-3 mt-4">
            <p className="text-sm text-gray-600">Marque os serviços que você realiza ({totalMarcados} selecionados):</p>
            {Object.entries(grupos).map(([cat, lista]) => (
              <Card key={cat}>
                <CardHeader className="py-2"><CardTitle className="text-sm text-emerald-800">{cat}</CardTitle></CardHeader>
                <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-1 pb-3">
                  {lista.map((s) => (
                    <label key={s.id} className="flex items-center gap-2 text-sm py-1 cursor-pointer">
                      <input type="checkbox" checked={s.marcado} onChange={() => toggleServico(s.id)} className="h-4 w-4 accent-emerald-700" data-testid={`servico-check-${s.id}`} />
                      {s.nome}{s.unidade === 'm2' && <span className="text-xs text-gray-400">(por m²)</span>}
                    </label>
                  ))}
                </CardContent>
              </Card>
            ))}
            <Button onClick={salvarServicos} disabled={salvando} className="w-full text-white" style={{ backgroundColor: '#2B7A3D' }} data-testid="salvar-servicos-btn">
              {salvando ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Salvar serviços'}
            </Button>
          </TabsContent>

          <TabsContent value="dados" className="space-y-4 mt-4">
            <Card>
              <CardHeader className="py-3"><CardTitle className="text-sm">Contato e PIX</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                <div className="space-y-1">
                  <Label className="text-xs">Telefone</Label>
                  <Input value={perfil.telefone} onChange={(e) => setPerfil({ ...perfil, telefone: e.target.value })} data-testid="perfil-telefone" />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-1">
                    <Label className="text-xs">Tipo de chave PIX</Label>
                    <Select value={perfil.pix_tipo || undefined} onValueChange={(v) => setPerfil({ ...perfil, pix_tipo: v })}>
                      <SelectTrigger data-testid="perfil-pix-tipo"><SelectValue placeholder="Selecione" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="cpf">CPF</SelectItem>
                        <SelectItem value="telefone">Telefone</SelectItem>
                        <SelectItem value="email">E-mail</SelectItem>
                        <SelectItem value="aleatoria">Aleatória</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Chave PIX</Label>
                    <Input value={perfil.pix_chave} onChange={(e) => setPerfil({ ...perfil, pix_chave: e.target.value })} data-testid="perfil-pix-chave" />
                  </div>
                </div>
                <Button onClick={salvarPerfil} className="w-full text-white" style={{ backgroundColor: '#2B7A3D' }} data-testid="salvar-perfil-btn">Salvar dados</Button>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="py-3"><CardTitle className="text-sm flex items-center gap-1"><KeyRound size={14} /> Alterar senha</CardTitle></CardHeader>
              <CardContent className="space-y-2">
                <Input type="password" placeholder="Senha atual" value={senhas.atual} onChange={(e) => setSenhas({ ...senhas, atual: e.target.value })} data-testid="senha-atual" />
                <Input type="password" placeholder="Nova senha (mín. 6)" value={senhas.nova} onChange={(e) => setSenhas({ ...senhas, nova: e.target.value })} data-testid="senha-nova" />
                <Button variant="outline" onClick={alterarSenha} className="w-full" data-testid="alterar-senha-btn">Alterar senha</Button>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="os" className="space-y-2 mt-4">
            {ordens.length === 0 && <p className="text-sm text-gray-400 text-center py-6">Nenhuma OS atribuída a você ainda.</p>}
            {ordens.map((o) => (
              <Card key={o.id} data-testid={`os-card-${o.id}`}>
                <CardContent className="py-3 flex items-start justify-between gap-2">
                  <div>
                    <p className="font-medium text-sm">{o.servico_nome}</p>
                    <p className="text-xs text-gray-500">{formatDate(o.data_agendamento)} {o.hora_agendamento && `às ${o.hora_agendamento}`} · {o.cliente_nome}</p>
                    <p className="text-xs text-gray-400">{o.endereco}{o.cidade ? ` — ${o.cidade}` : ''}</p>
                  </div>
                  <Badge className={STATUS_COLORS[o.status] || 'bg-gray-100 text-gray-600'}>{o.status}</Badge>
                </CardContent>
              </Card>
            ))}
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
